import { amplifyClient, getEndpointV2 } from 'api';
import { createApiSlice } from 'helpers/createApiSlice';
import { makeReshaper } from 'helpers/makeReshaper';
import { call, put, select } from 'redux-saga/effects';
import { userFetch } from 'store/features/userFetch';
import { AnyRecord, Company, UserData, UserRfcMeta } from 'types';

import { companyFetch } from './companyFetch';
import { rfcCompanyNameUpdate } from './rfcCompanyNameUpdate';
import { uploadCompanyRfcMeta } from './utils/uploadCompanyRfcMeta';

const path = '/auth/company/:companyId/rfc/:code/metadata';

const rfcMetaReshaper = makeReshaper({
  LugarExpedicion: 'expeditionPlace',
  RegimenFiscal: 'taxRegime',
  ConstanciaFiscal: 'taxCertificate',
  RazonSocial: 'companyName',
});

const matchMetaReshaper = makeReshaper({
  company: 'company',
  matchAll: 'match',
  matchPostalCode: 'matching_values.LugarExpedicion',
  matchTaxRegime: 'matching_values.RegimenFiscal',
  matchRfcCompanyName: 'matching_values.RazonSocial',
  matchRfcCode: 'matching_values.code',
});

interface AddUpdateResponse {
  company: Company;
  matchAll: boolean;
  matchPostalCode: boolean;
  matchTaxRegime: boolean;
  matchRfcCompanyName: boolean;
  matchRfcCode: boolean;
}

interface AddUpdateData extends AddUpdateResponse {
  forceToProcess?: boolean;
}

type AddUpdatePayload = {
  rfcMeta: UserRfcMeta;
  code: string;
  forceToProcess?: boolean;
};

export const companyAddRfcMeta = createApiSlice<AddUpdateData, AddUpdatePayload>({
  name: 'companyAddRfcMeta',
  *tryGenerator(_, { payload }) {
    const { companyId } = (yield select(userFetch.selectors.getData)) as UserData;
    const { rfcMeta, code, forceToProcess = false } = payload;

    const dataToUpload: UserRfcMeta = yield call(uploadCompanyRfcMeta, rfcMeta);

    const responseDto: { data: AnyRecord } = yield call(amplifyClient, {
      method: 'post',
      url: getEndpointV2({ path, urlParams: { companyId, code } }),
      data: {
        rfcData: rfcMetaReshaper(dataToUpload),
        process: forceToProcess,
      },
    });

    const response = matchMetaReshaper(responseDto.data) as AddUpdateResponse;

    if (forceToProcess || response.matchAll) {
      const { companyName } = rfcMeta;
      yield put(rfcCompanyNameUpdate.actions.call({ code, companyName }));

      yield put(companyFetch.actions.call());
    }

    return {
      ...response,
      forceToProcess,
    };
  },
});

export const companyUpdateRfcMeta = createApiSlice<AddUpdateData, AddUpdatePayload>({
  name: 'companyUpdateRfcMeta',
  *tryGenerator(_, { payload }) {
    const { companyId } = (yield select(userFetch.selectors.getData)) as UserData;
    const { rfcMeta, code, forceToProcess = false } = payload;

    const dataToUpload: UserRfcMeta = yield call(uploadCompanyRfcMeta, rfcMeta);

    const responseDto: { data: AnyRecord } = yield call(amplifyClient, {
      method: 'put',
      url: getEndpointV2({ path, urlParams: { companyId, code } }),
      data: {
        rfcData: rfcMetaReshaper(dataToUpload),
        process: forceToProcess,
      },
    });

    const response = matchMetaReshaper(responseDto.data) as AddUpdateResponse;

    if (forceToProcess || response.matchAll) {
      const { companyName } = rfcMeta;
      yield put(rfcCompanyNameUpdate.actions.call({ code, companyName }));

      yield put(companyFetch.actions.call());
    }

    return {
      ...response,
      forceToProcess,
    };
  },
});

type DeletePayload = {
  code: string;
};

export const companyDeleteRfcMeta = createApiSlice<unknown, DeletePayload>({
  name: 'companyDeleteRfcMeta',
  *tryGenerator(_, { payload }) {
    const { companyId } = (yield select(userFetch.selectors.getData)) as UserData;
    const { code } = payload;

    yield call(amplifyClient, {
      method: 'del',
      url: getEndpointV2({ path, urlParams: { companyId, code } }),
    });

    yield put(companyFetch.actions.call());

    return null;
  },
});
